/**
 * Sales & marketing template library.
 *
 * Templates are immutable defaults. The user fills `vars` and (optionally)
 * overrides subject/body — overrides + vars are what gets persisted to a
 * SalesAsset on the backend. Rendering = substituting {{var}} tokens.
 */

// Substitute {{var}} tokens. Unknown variables stay as {{var}} so users
// notice they forgot to fill something.
export function substitute(text, vars) {
  if (!text) return '';
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const v = vars?.[key];
    return v == null || v === '' ? match : String(v);
  });
}

// Pull every {{var}} referenced inside a template — used by the editor to
// know which input fields to render.
export function extractVars(template) {
  const texts = [
    template.subject || '',
    template.body || '',
    template.ctaLabel || '',
    template.ctaUrl || '',
    ...(template.hashtags || []),
  ];
  const found = new Set();
  for (const t of texts) {
    const matches = t.match(/\{\{(\w+)\}\}/g) || [];
    for (const m of matches) found.add(m.replace(/[{}]/g, ''));
  }
  return [...found];
}

// Friendly labels + placeholders for the variable inputs.
export const VAR_META = {
  agencyName: { label: 'Your agency name', placeholder: 'Acme Marketing Co.' },
  senderName: { label: 'Your name', placeholder: 'Jane Doe' },
  senderEmail: { label: 'Your email', placeholder: 'jane@acme.com' },
  senderTitle: { label: 'Your title', placeholder: 'Founder, GEO Strategist' },
  prospectName: { label: 'Prospect first name', placeholder: 'Alex' },
  prospectCompany: { label: 'Prospect company', placeholder: 'Bright Dental' },
  prospectDomain: { label: 'Prospect domain', placeholder: 'brightdental.com' },
  auditLink: { label: 'Your audit / signup link', placeholder: 'https://acme.com/free-audit' },
  caseStudyMetric: {
    label: 'Case-study metric',
    placeholder: '23x AI traffic in 10 months',
  },
  category: { label: 'Industry / category', placeholder: 'dentists in NYC' },
};

// ---------- Email templates ----------

export const EMAIL_TEMPLATES = [
  {
    id: 'cold-local',
    audience: 'Local business',
    label: 'Cold — local business',
    description:
      "Opens with a specific 'AI engines aren't citing you' hook for a brick-and-mortar.",
    subject: "Your business isn't showing up in ChatGPT (yet)",
    body: `Hi {{prospectName}},

I run an audit tool that scores how visible local businesses are inside AI search engines (ChatGPT, Claude, Perplexity, Gemini, Copilot).

Quick check: when I asked ChatGPT about "{{category}}" near you, {{prospectCompany}} didn't come up. Your competitors did.

That matters because AI engines are quietly replacing the top of Google's funnel — 40%+ of consumer queries are starting in an AI chat now, not a search box. If your site isn't structured for them, you're effectively invisible to that traffic.

I put together a free 60-second AEO scorecard that tells you:
  - whether AI crawlers can read your site at all
  - which schema markup you're missing
  - the questions buyers ask AI that you're not answering yet

Worth seeing yours? Takes a minute → {{auditLink}}

— {{senderName}}
{{senderTitle}} · {{agencyName}}`,
    ctaLabel: 'Run my free AEO audit',
    ctaUrl: '{{auditLink}}',
  },
  {
    id: 'cold-ecom',
    audience: 'E-commerce',
    label: 'Cold — e-commerce',
    description: 'Conversion-led, focuses on AI sending shoppers to competitors.',
    subject: 'ChatGPT is sending shoppers to your competitors',
    body: `Hi {{prospectName}},

When buyers ask AI engines "best {{category}}" or "alternatives to [brand]", they're not getting 10 blue links anymore — they're getting a short list of names, with citations.

We pulled the AI-citation profile for {{prospectDomain}} and a few of your competitors. Short version: their product pages have structured Product + AggregateRating + FAQ schema. Yours don't. So ChatGPT and Perplexity are recommending them as "best in category" while skipping you.

That's a fixable problem — usually 1-2 weeks of structural work, no new content needed.

If you want the full comparison (free, no pitch), reply with "send it" and I'll drop it in your inbox tomorrow.

— {{senderName}}
{{agencyName}}`,
    ctaLabel: 'Send me the comparison',
    ctaUrl: '{{auditLink}}',
  },
  {
    id: 'cold-saas',
    audience: 'SaaS / B2B',
    label: 'Cold — SaaS',
    description: 'Targets SaaS founders/marketers; pre-qualified buyer angle.',
    subject: 'Is {{prospectCompany}} missing from AI answers in your category?',
    body: `Hi {{prospectName}},

I'm seeing a pattern in B2B SaaS: AI traffic converts 2-3× higher than Google traffic — buyers arrive pre-sold because the AI already vetted you.

But that only works if AI engines actually cite you. When users ask Claude or Perplexity "best tool for {{category}}", they pull from a short list — usually 3-5 vendors with very specific content patterns (FAQ schema, entity clarity, author bylines, llms.txt).

I checked {{prospectDomain}}. You're missing 4 of those 5. None are hard to fix.

15-minute call this week? I'll walk you through the gaps and you can decide whether to do it in-house or with us. No deck, no pitch — just the gap analysis.

{{auditLink}}

— {{senderName}}
{{senderTitle}}, {{agencyName}}`,
    ctaLabel: 'Grab a 15-min slot',
    ctaUrl: '{{auditLink}}',
  },
  {
    id: 'agency-partner',
    audience: 'Agency partner',
    label: 'Agency partner — white-label offer',
    description: 'Targets other agencies — adds AEO as a new $1k+/mo service.',
    subject: 'Add a $1,500/mo service to {{prospectCompany}} (no extra headcount)',
    body: `Hi {{prospectName}},

You probably already get the "we want to show up in ChatGPT" question from clients. Most agencies are still saying "we'll figure it out" — which is creating an opening.

We white-label a full AEO (AI Engine Optimization) stack to agencies: audit + content rewrite + schema deployment + monthly reporting, all under your brand. Your clients see your logo on every report; you keep the markup.

A small agency partner of ours bills {{caseStudyMetric}} into their existing retainers — no extra headcount, ~4 hours/month of QA work.

If that sounds interesting, the deck + pricing is here: {{auditLink}}

Happy to run a sample audit on a domain of yours so you can see what your clients would receive.

— {{senderName}}
{{agencyName}}`,
    ctaLabel: 'See partner pricing',
    ctaUrl: '{{auditLink}}',
  },
  {
    id: 'warm-audit',
    audience: 'Warm intro',
    label: 'Warm — free audit invite',
    description: 'Low-friction "want a free score?" message for a warm contact.',
    subject: 'Quick AEO score for {{prospectCompany}}?',
    body: `Hey {{prospectName}},

Built a thing that scores how visible a site is to AI search engines. Takes ~60 seconds, gives you a 0-100 score across 7 pillars (schema, FAQ structure, author signals, etc.) plus the top recommendations.

Want me to run it on {{prospectDomain}} and send the report? No pitch attached — I just like seeing real-world scores.

— {{senderName}}`,
    ctaLabel: 'Yes, send the score',
    ctaUrl: '{{auditLink}}',
  },
  {
    id: 'followup',
    audience: 'Follow-up',
    label: 'Follow-up — no response',
    description: 'Short, polite bump after 5-7 days of silence.',
    subject: 'Re: ChatGPT visibility for {{prospectCompany}}',
    body: `Hi {{prospectName}},

Following up — no worries if the timing's off.

If it helps, I can just send the 1-page audit summary for {{prospectDomain}} and you can sit on it. Worst case you ignore it; best case it flags something useful.

Reply "send" or "no thanks" and I'll know which way to go.

— {{senderName}}`,
    ctaLabel: 'Reply "send"',
    ctaUrl: '',
  },
];

// ---------- Social post templates ----------

export const SOCIAL_TEMPLATES = [
  // LinkedIn
  {
    id: 'li-1',
    platform: 'linkedin',
    label: 'Why traditional SEO is dying',
    body: `Traditional SEO assumes someone types a query, sees 10 blue links, and clicks one.

ChatGPT skips steps 2 and 3.

The new top-of-funnel is a 1-paragraph answer with 3 citations. If your site isn't structured to be one of those citations, you're not in the funnel at all.

The fix isn't more content. It's restructuring what you already have:

→ Add FAQPage + Article schema (most sites have neither)
→ Surface 2-3 "answer capsules" per page — short factual statements AI can lift verbatim
→ Publish /llms.txt at your root (it's robots.txt for AI crawlers)
→ Add author bylines + publish dates everywhere

7-pillar GEO audit usually finds 10+ fixes in 24 hours of work.

If you want one for your site, reply "audit" — first 5 are free this week.

#GEO #AEO #SEO #AISearch`,
    hashtags: ['GEO', 'AEO', 'SEO', 'AISearch'],
  },
  {
    id: 'li-2',
    platform: 'linkedin',
    label: 'Case study — 23× AI traffic',
    body: `Case study from a client this quarter:

→ AI search traffic: +23× year-over-year
→ Conversions from AI sources: +27×
→ Perplexity visibility score: +75 points
→ ChatGPT visibility: 51 → 73

Timeline: under 10 months.
Net new content created: zero.

What changed: every existing page got restructured for AI citation — FAQ schema, answer capsules, clean H1→H2→H3 hierarchy, author attribution, /llms.txt.

The "compounding moat" effect is real. Every month you stay ahead, your citations get reinforced. Every month your competitors wait, the gap widens.

If you want the playbook, comment "playbook" and I'll send it.`,
    hashtags: ['GEO', 'AEO', 'CaseStudy'],
  },
  {
    id: 'li-3',
    platform: 'linkedin',
    label: '5 schema types every site needs',
    body: `5 JSON-LD schema types every site should have in 2026 (in priority order):

1️⃣ Article — on every blog post / long-form page
2️⃣ FAQPage — on at least your top 10 pages by traffic
3️⃣ Organization — once, on your homepage
4️⃣ BreadcrumbList — on every non-root page
5️⃣ AggregateRating — if you have 10+ reviews anywhere

Most sites I audit have zero of these. The rest have Article only.

Adding all 5 takes a developer 1-2 days. AI engines start citing you noticeably more within ~3 weeks of the next crawl.

It is the highest ROI work in marketing right now. Schema. Markup.

Save this post for your dev team.`,
    hashtags: ['SchemaMarkup', 'JSONLD', 'AEO'],
  },
  {
    id: 'li-4',
    platform: 'linkedin',
    label: 'The new search funnel',
    body: `The old search funnel:

Query → 10 blue links → Click 3 → Read → Decide

The new search funnel:

Query → 1 AI answer with 3 citations → Click maybe → Decide

You're not competing for click-through rate anymore. You're competing to BE the citation.

That's a completely different optimization target. CTR-led SEO (compelling titles, meta descriptions) doesn't help. What helps:

✓ Direct, declarative answers in the first 200 words
✓ Schema that tells the AI what kind of page this is
✓ Author/date/source attribution AI engines treat as trust signals
✓ Specific numbers and named entities (AIs prefer quoting these)

If your team is still grading content by H1 length and meta-description CTR, you're optimizing for a funnel that's evaporating.`,
    hashtags: ['AISearch', 'GEO', 'MarketingStrategy'],
  },
  {
    id: 'li-5',
    platform: 'linkedin',
    label: 'Hire for AEO, not SEO',
    body: `If you're hiring an SEO in 2026, ask one question in the interview:

"How would you optimize a page so ChatGPT cites it as an answer?"

If they talk about backlinks, keyword density, or meta descriptions — wrong era.

If they talk about schema markup, answer capsules, entity clarity, and llms.txt — you've found the right person.

The skill set hasn't disappeared. It's split. Traditional SEO still matters (Google isn't going anywhere). But AEO is a separate discipline and most candidates haven't caught up yet.

Pay 15-20% more for someone who has. They'll pay for themselves inside 2 quarters.`,
    hashtags: ['Hiring', 'SEO', 'AEO'],
  },
  // Twitter / X
  {
    id: 'tw-1',
    platform: 'twitter',
    label: 'AI traffic replacing Google traffic',
    body: `AI traffic is replacing Google traffic.

The companies winning aren't writing more content. They're restructuring what they already have so ChatGPT, Claude, and Perplexity can quote them.

Three patterns I keep seeing in the winners:

1/ FAQ schema on every important page
2/ "Answer capsules" — short factual statements AI lifts verbatim
3/ Clean entity signals (Organization + Person + sameAs)

That's it. Most of the AEO win comes from those three.`,
    hashtags: ['AEO', 'GEO'],
  },
  {
    id: 'tw-2',
    platform: 'twitter',
    label: 'Quick tip — FAQ schema',
    body: `Free 10-minute win for any marketing site:

Add FAQPage JSON-LD schema to your top 10 pages.

Just the 5 most-asked questions per page with their answers, wrapped in Question / Answer types.

AI engines aggressively prefer pages with FAQ schema as citation sources. The lift is measurable within ~3 weeks.

Cost: an hour of dev work. Return: ongoing.`,
    hashtags: ['SchemaMarkup', 'SEO'],
  },
  {
    id: 'tw-3',
    platform: 'twitter',
    label: 'Authors matter',
    body: `Quick stat from this week's analysis:

ChatGPT cites pages with explicit author bylines 3.4× more often than pages without.

Adding <meta name="author"> and rel="author" linked to a Person schema is a 5-minute change.

Why most sites skip it: there's no Google-side reward, so it never made the priority list.

There's a massive AI-side reward.`,
    hashtags: ['AEO', 'GEO'],
  },
  {
    id: 'tw-4',
    platform: 'twitter',
    label: 'Case study micro-thread',
    body: `Client snapshot:

Before AEO work: 0 AI referrals/mo
After 10 weeks: ~4,200 AI referrals/mo
Conversion rate on AI traffic: 3.1× vs Google

What changed: 20 pages rewritten with answer-first structure + FAQ schema + author bylines.

Cost: roughly 40 hours of structural work.

The asymmetry is wild. Almost nobody's doing this yet.`,
    hashtags: ['AEO', 'Marketing'],
  },
  {
    id: 'tw-5',
    platform: 'twitter',
    label: 'Three signs you\'re invisible to AI',
    body: `Three signs your site is invisible to AI engines:

1/ Open robots.txt → no User-agent: GPTBot section
2/ View page source → no <script type="application/ld+json">
3/ /llms.txt returns 404

If all three are true, you're not in the AI-search consideration set at all.

All three are fixable in under a day.`,
    hashtags: ['SEO', 'AEO'],
  },
  // Facebook
  {
    id: 'fb-1',
    platform: 'facebook',
    label: 'Educational — what is GEO/AEO?',
    body: `What is GEO / AEO?

GEO = Generative Engine Optimization.
AEO = Answer Engine Optimization.

Same idea, different names: getting your business cited inside AI tools like ChatGPT, Claude, Perplexity, and Google's AI Overviews.

It's where SEO was in 2003 — early enough that a small team can win, late enough that the playbook is starting to crystallize.

If your customers ask AI before they ask Google (they do — even your customers in their 60s do now), you have to play this game.

Want a free 60-second AEO score for your site? Comment "audit" and I'll send the link.`,
  },
  {
    id: 'fb-2',
    platform: 'facebook',
    label: 'Local business angle',
    body: `Local business owners: a quick test.

Open ChatGPT. Ask:

"Best [your type of business] in [your city]"

If your business isn't named in the answer, that's a problem you can fix this month.

I help local businesses get cited by ChatGPT, Claude, and Perplexity. It's not magic — it's structural fixes to your existing site (schema markup, FAQ sections, opening hours data, etc.).

Free audit + score: comment "score" below.`,
  },
  {
    id: 'fb-3',
    platform: 'facebook',
    label: 'Service promo',
    body: `New service we're rolling out: AEO Audits.

What's included:
✓ 7-pillar audit of your site (schema, FAQs, headings, llms.txt, more)
✓ Per-page scores out of 100
✓ Prioritized list of fixes (high → low impact)
✓ Side-by-side comparison vs 2-3 of your competitors

Turnaround: 48 hours. Price: free for the first 10 sign-ups this month.

DM us to grab a slot.`,
  },
  {
    id: 'fb-4',
    platform: 'facebook',
    label: 'Q&A engagement',
    body: `Question for our community:

When was the last time you asked ChatGPT, Claude, or Perplexity a question about something you were considering buying?

A) Daily / weekly
B) Occasionally
C) Rarely
D) Never

We're seeing this shift faster than most people realize. Drop your letter in the comments — curious to see the split.`,
  },
  {
    id: 'fb-5',
    platform: 'facebook',
    label: 'Behind the scenes',
    body: `Behind the scenes at {{agencyName}} this week:

We ran AEO audits on 12 client sites. Top finding? 9 out of 12 had ZERO schema markup. Not "the wrong schema". Just none.

That's an easy win — usually 2-3 days of work, then measurable AI-citation lift inside a month.

If you've been wondering why your traffic from AI sources is flat, schema is probably your single biggest lever.

Want us to check yours? Reply 👋`,
  },
];

// ---------- Service menu defaults ----------

export const DEFAULT_SERVICE_MENU = [
  {
    id: 'starter',
    name: 'Starter',
    price: 497,
    currency: 'USD',
    billing: 'monthly',
    audience: 'Small business / 1 brand',
    description: 'Get found by AI engines for one website.',
    features: [
      '1 domain',
      'Monthly 7-pillar AEO audit',
      '5 page rewrites per month',
      'Basic monthly report (PDF)',
      'Email support · 48hr response',
    ],
    isPopular: false,
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 997,
    currency: 'USD',
    billing: 'monthly',
    audience: 'Multi-brand / agency-light',
    description: 'Compound visibility across multiple properties.',
    features: [
      'Up to 3 domains',
      'Weekly audits + score history',
      '25 page rewrites per month',
      'White-label client reports',
      'AI content gap analysis (monthly)',
      'Competitive benchmarking (3 competitors)',
      'Priority email support · 24hr response',
    ],
    isPopular: true,
  },
  {
    id: 'agency',
    name: 'Agency Partner',
    price: 2497,
    currency: 'USD',
    billing: 'monthly',
    audience: 'Full-service agencies',
    description: 'Resell AEO as a service under your own brand.',
    features: [
      'Unlimited domains',
      'Unlimited audits + content gaps',
      'Full white-label (your logo, your colors)',
      'Schema template library (15+ types)',
      'Shareable client report links',
      'Dedicated Slack channel',
      'Quarterly strategy review',
    ],
    isPopular: false,
  },
];

// ---------- Helpers ----------

export const EMAIL_AUDIENCES = [...new Set(EMAIL_TEMPLATES.map((t) => t.audience))];
export const SOCIAL_PLATFORMS = ['linkedin', 'twitter', 'facebook'];

export function getEmailTemplate(id) {
  return EMAIL_TEMPLATES.find((t) => t.id === id);
}
export function getSocialTemplate(id) {
  return SOCIAL_TEMPLATES.find((t) => t.id === id);
}
